import type { GradingTreePayload } from '../components/Workspace/admin/grading/types';

export type RoleClusterMeta = {
  clusterId: number;
  clusterName: string;
  familyId: number;
  familyName: string;
  minGrade: number;
  maxGrade: number;
  roleNames: string[];
};

function isVisible(entity: { is_visible?: boolean }): boolean {
  return entity.is_visible !== false;
}

/** Метаданные кластеров: диапазон грейдов и список ролей из дерева грейдинга. */
export function buildRoleClusterMetaList(tree: GradingTreePayload): RoleClusterMeta[] {
  const familyNameById = new Map(
    tree.families.filter(isVisible).map((family) => [Number(family.id), String(family.name).trim()]),
  );

  const rolesByCluster = new Map<number, typeof tree.roles>();
  for (const role of tree.roles) {
    if (!isVisible(role)) continue;
    const clusterId = Number(role.role_cluster_id);
    const list = rolesByCluster.get(clusterId);
    if (list) list.push(role);
    else rolesByCluster.set(clusterId, [role]);
  }

  const metas: RoleClusterMeta[] = [];

  for (const cluster of tree.roleClusters) {
    if (!isVisible(cluster)) continue;

    const familyId = Number(cluster.family_id);
    const familyName = familyNameById.get(familyId) || '';
    if (!familyName) continue;

    const roles = rolesByCluster.get(Number(cluster.id)) || [];
    const grades = roles
      .map((role) => Number(role.grade_level))
      .filter((grade) => Number.isFinite(grade));

    if (grades.length === 0) continue;

    const roleNames = Array.from(
      new Set(roles.map((role) => String(role.name || '').trim()).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, 'ru'));

    metas.push({
      clusterId: Number(cluster.id),
      clusterName: String(cluster.name).trim(),
      familyId,
      familyName,
      minGrade: Math.min(...grades),
      maxGrade: Math.max(...grades),
      roleNames,
    });
  }

  return metas.sort((a, b) => {
    const byFamily = a.familyName.localeCompare(b.familyName, 'ru');
    if (byFamily !== 0) return byFamily;
    return a.clusterName.localeCompare(b.clusterName, 'ru');
  });
}

export function buildRoleClusterMetaMap(metas: RoleClusterMeta[]): Map<number, RoleClusterMeta> {
  return new Map(metas.map((meta) => [meta.clusterId, meta]));
}
